import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button/button';
// eslint-disable-next-line jira/restricted/@atlaskit+dropdown-menu
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ChevronDown from '@atlaskit/icon/core/chevron-down';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl, defineMessage } from '@jira/platform__react-intl';
import { getNewEditorLink } from '@jira/project-settings__project-configuration-links/src/getNewEditorLink.tsx';

const openInNewEditor = defineMessage({
	id: 'workflow-editor-preference-settings.open-in-new-editor',
	defaultMessage: 'Open workflow in new editor',
	description: 'Link that opens workflow in new workflow editor',
});

const switchEditorButton = defineMessage({
	id: 'workflow-editor-preference-settings.switch-editor-button',
	defaultMessage: 'Switch editor',
	description: 'Button that allows you to open dropdown with option to switch editor',
});

type Props = {
	workflowId: string;
	workflowName: string;
	editMode: 'draft' | 'live';
};

export const WorkflowEditorPreferenceDropdown = ({
	workflowId,
	editMode,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireTrackEvent = useCallback(
		(mode: string) => {
			const dropdownItemClickEvent = createAnalyticsEvent({
				action: 'click',
				actionSubject: 'dropdownItem',
			});
			fireUIAnalytics(dropdownItemClickEvent, 'workflowEditorToolbarNewEditorLink', {
				workflowLegacyEditMode: mode === 'draft' ? 'Draft' : 'Live',
			});
		},
		[createAnalyticsEvent],
	);

	return (
		<>
			<DropdownMenu
				testId="workflow-editor-preference-settings.dropdown-menu"
				placement="bottom-end"
				trigger={({ triggerRef, ...props }) => (
					<Button {...props} ref={triggerRef} iconAfter={<ChevronDown label="" size="small" />}>
						<LegacyButtonText>{formatMessage(switchEditorButton)}</LegacyButtonText>
					</Button>
				)}
				shouldRenderToParent
			>
				<DropdownItemGroup>
					<DropdownItem
						href={getNewEditorLink(workflowId)}
						onClick={() => fireTrackEvent(editMode)}
						UNSAFE_shouldDisableRouterLink
					>
						{formatMessage(openInNewEditor)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegacyButtonText = styled.span({
	fontWeight: token('font.weight.regular'),
});
